import { useTranslation } from "react-i18next";
import React, { useState, useRef, useEffect } from "react";
import { DataNotFound } from "../common/DataNotFound";

interface CreatableSelectProps {
  label: string;
  placeholder?: string;
  required?: boolean;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  onAddOption: (newOption: string) => void;
  error?: string | null;
  onBlur?: () => void;
}

export const CreatableSelect: React.FC<CreatableSelectProps> = ({
  label,
  placeholder,
  required = false,
  options,
  value,
  onChange,
  onAddOption,
  error,
  onBlur,
}) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [dataList, setDataList] = useState<string[]>(options);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync internal options when parent props update
  useEffect(() => {
    setDataList(options);
  }, [options]);

  // Click Outside Handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        if (onBlur) onBlur();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onBlur]);

  // Filter options based on search query
  const filteredOptions = dataList.filter((opt) =>
    opt.toLowerCase().includes(search.toLowerCase()),
  );

  // Check if search query exactly matches an existing option
  const isExactMatch = dataList.some(
    (opt) => opt.toLowerCase() === search.trim().toLowerCase(),
  );

  const handleSelect = (selectedVal: string) => {
    onChange(selectedVal);
    setSearch("");
    setIsOpen(false);
  };

  const handleCreate = () => {
    const trimmed = search.trim();
    if (!trimmed) return;

    if (!dataList.includes(trimmed)) {
      setDataList((prev) => [...prev, trimmed]);
      onAddOption(trimmed);
    }
    onChange(trimmed);
    setSearch("");
    setIsOpen(false);
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      {/* Label */}
      <label className="block text-xs font-semibold text-[rgb(var(--text-primary))] mb-1.5">
        {label} {required && <span className="text-red-600">*</span>}
      </label>

      {/* Input / Control Field */}
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full flex items-center justify-between px-3 py-2.5 bg-[rgb(var(--card))] border ${
          error ? "border-red-500" : "border-[rgb(var(--border))]"
        } rounded-lg cursor-pointer text-xs transition-colors`}
      >
        <span
          className={
            value
              ? "text-[rgb(var(--text-primary))]"
              : "text-[rgb(var(--text-muted))]"
          }
        >
          {value || placeholder || t("select.selectOrAdd")}
        </span>

        <svg
          className={`w-4 h-4 text-[rgb(var(--text-muted))] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
          {/* Search Box */}
          <div className="p-2 border-b border-[rgb(var(--border))] bg-[rgb(var(--card))]">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("select.searchOrAdd")}
              className="w-full px-2.5 py-1.5 bg-[rgb(var(--card))] border border-[rgb(var(--border))] rounded-lg text-xs text-[rgb(var(--text-primary))] focus:outline-none focus:border-[rgb(var(--text-accent))]"
            />
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-44 p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSelect(item)}
                  className={`px-3 py-2 text-xs rounded-lg cursor-pointer transition-colors ${
                    value === item
                      ? "bg-[rgb(var(--card-muted))] font-semibold text-[rgb(var(--text-primary))]"
                      : "text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--card-muted))]"
                  }`}
                >
                  {item}
                </div>
              ))
            ) : !search.trim() ? (
              <DataNotFound inline title={t("select.noOptions")} />
            ) : null}

            {/* Create New Option Button */}
            {search.trim() && !isExactMatch && (
              <div
                onClick={handleCreate}
                className="px-3 py-2 text-xs text-[rgb(var(--text-accent))] hover:bg-[rgb(var(--card-muted))] font-medium rounded-lg cursor-pointer border-t border-[rgb(var(--border))] mt-1 flex items-center gap-1"
              >
                <span>+ {t("select.add")}</span>
                <span className="font-semibold text-[rgb(var(--text-accent))]">
                  "{search.trim()}"
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};
